/**
 * PlanetInfoPanel — right side of the panel.
 *
 * Sections (top to bottom):
 *   1. Planet header (name, biome, constellation tier)
 *   2. Your operation: credits, outpost/station counts, buffer, claim button
 *   3. Selected zone: coords + structure detail OR "Build" accordion
 *   4. Recent activity log
 *   5. Leave panel button
 */

import { useState, useEffect, useRef } from 'react';
import { usePlanetPanelStore, type StructureWire, type MiningStatsWire } from './state/planetPanelStore';
import { PlanetActivityLog } from './PlanetActivityLog';
import { networkManager } from '../../systems/NetworkManager';
import {
  PLANET_BALANCE,
  ORGANIC_RESOURCES,
  CRYSTAL_RESOURCES,
  latLonToHexId,
  withinRings,
  type HexFrequency,
} from '@astra/shared';

interface Props {
  onLeave(): void;
}

type StructureType = 'outpost' | 'mining_station' | 'relay' | 'scanner';

const COSTS: Record<StructureType, number> = {
  outpost:        PLANET_BALANCE.OUTPOST_COST,
  mining_station: PLANET_BALANCE.MINING_STATION_COST,
  relay:          PLANET_BALANCE.RELAY_COST,
  scanner:        PLANET_BALANCE.SCANNER_COST,
};

const STRUCTURE_LABELS: Record<StructureType, string> = {
  outpost:        'Outpost',
  mining_station: 'Mining Station',
  relay:          'Relay',
  scanner:        'Scanner',
};

const STRUCTURE_COLORS: Record<StructureType, string> = {
  outpost:        '#5599ff',
  mining_station: '#ffaa44',
  relay:          '#44dddd',
  scanner:        '#bb77ff',
};

export function PlanetInfoPanel({ onLeave }: Props) {
  const planet = usePlanetPanelStore((s) => s.planet);
  const structures = usePlanetPanelStore((s) => s.structures);
  const credits = usePlanetPanelStore((s) => s.credits);
  const selection = usePlanetPanelStore((s) => s.selection);
  const selfId = usePlanetPanelStore((s) => s.selfPlayerId);
  const miningStats = usePlanetPanelStore((s) => s.miningStats);
  const error = usePlanetPanelStore((s) => s.lastError);

  // Loading states
  const [pendingBuild, setPendingBuild] = useState<StructureType | null>(null);
  const [pendingDismantle, setPendingDismantle] = useState<string | null>(null);
  const [pendingToggle, setPendingToggle] = useState(false);
  const [pendingClaim, setPendingClaim] = useState(false);
  const [buildOpen, setBuildOpen] = useState(false);

  // Track structure count to detect server response (plant / dismantle)
  const prevStructureCount = useRef(structures.length);
  useEffect(() => {
    if (structures.length !== prevStructureCount.current) {
      setPendingBuild(null);
      setPendingDismantle(null);
      prevStructureCount.current = structures.length;
    }
  }, [structures.length]);

  // Clear pending claim when buffer totals change
  const bufferTotal = structures.reduce((s, o) => {
    if (o.structure_type !== 'outpost' || o.owner_player_id !== selfId) return s;
    return s + Object.values(o.buffer_contents ?? {}).reduce((a, v) => a + (typeof v === 'number' ? v : 0), 0);
  }, 0);
  const prevBuffer = useRef(bufferTotal);
  useEffect(() => {
    if (bufferTotal !== prevBuffer.current) {
      setPendingClaim(false);
      prevBuffer.current = bufferTotal;
    }
  }, [bufferTotal]);

  // Clear pending toggle when the selected structure's active state changes
  const selectedStruct = selection?.clickedStructureId
    ? structures.find((s) => s.id === selection.clickedStructureId)
    : null;
  const selectedActive = selectedStruct?.is_active;
  const prevSelectedActive = useRef(selectedActive);
  useEffect(() => {
    if (selectedActive !== prevSelectedActive.current) {
      setPendingToggle(false);
      prevSelectedActive.current = selectedActive;
    }
  }, [selectedActive]);

  // Clear loading on error
  useEffect(() => {
    if (error) {
      setPendingBuild(null);
      setPendingDismantle(null);
      setPendingToggle(false);
      setPendingClaim(false);
    }
  }, [error]);

  // Close build accordion when selection changes
  useEffect(() => {
    setBuildOpen(false);
    setPendingBuild(null);
  }, [selection?.lat, selection?.lon]);

  if (!planet) {
    return (
      <div style={{ padding: 16, color: '#aaa', fontFamily: "'JetBrains Mono', monospace" }}>
        Loading planet data...
      </div>
    );
  }

  const myOutposts = structures.filter(
    (s) => s.structure_type === 'outpost' && s.owner_player_id === selfId
  );
  const myStations = structures.filter(
    (s) => s.structure_type === 'mining_station' && s.owner_player_id === selfId
  );
  const totalBuffer = myOutposts.reduce((s, o) => {
    return s + Object.values(o.buffer_contents ?? {}).reduce((a, v) => a + (typeof v === 'number' ? v : 0), 0);
  }, 0);
  const totalBufferCap = myOutposts.reduce((s, o) => s + (o.buffer_max ?? PLANET_BALANCE.OUTPOST_BUFFER_CAPACITY), 0);

  const selected = selection ? structureAt(structures, selection.clickedStructureId) : null;
  const planetFreq: HexFrequency = (planet.meshFrequency ?? 8) as HexFrequency;
  const inOwnOutpost = selection
    ? findOwnOutpostFor(structures, selfId, selection.lat, selection.lon, planet.radiusKm, planetFreq)
    : null;

  // Determine available build options for current selection
  const buildOptions: StructureType[] = [];
  if (selection && !selected) {
    if (!inOwnOutpost) {
      buildOptions.push('outpost');
    } else {
      buildOptions.push('mining_station', 'relay', 'scanner');
    }
  }

  const handleBuild = (type: StructureType) => {
    if (!selection || pendingBuild) return;
    setPendingBuild(type);
    // Phase 4+: derive hex_id from the selection on the planet's mesh
    // frequency. The server snaps lat/lon to the hex centroid.
    const hexId = latLonToHexId(selection.lat, selection.lon, planetFreq);
    networkManager.plantPlanetStructure(type, selection.lat, selection.lon, hexId);
  };

  const handleDismantle = (structureId: string) => {
    if (pendingDismantle) return;
    setPendingDismantle(structureId);
    networkManager.dismantlePlanetStructure(structureId);
  };

  const handleToggle = (structureId: string, active: boolean) => {
    if (pendingToggle) return;
    setPendingToggle(true);
    networkManager.togglePlanetStructure(structureId, active);
  };

  const handleClaimAll = () => {
    if (pendingClaim) return;
    setPendingClaim(true);
    for (const o of myOutposts) {
      networkManager.claimPlanetOutpostBuffer(o.id);
    }
  };

  return (
    <div style={panelStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#20a5a6' }}>
          {planet.name}
        </div>
        <div style={{ fontSize: 11, color: '#aaa' }}>
          {planet.planetType.toUpperCase()} · T{planet.constellationTier}
          {' · '}
          radius {(planet.radiusKm | 0).toLocaleString()} km
        </div>
      </div>

      {/* Your operation */}
      <Section title="Your Operation">
        <Row label="Credits"   value={credits.toLocaleString()} />
        <Row label="Outposts"  value={`${myOutposts.length}`} />
        <Row label="Stations"  value={`${myStations.length}`} />
        <Row label="Buffer"    value={`${Math.floor(totalBuffer)} / ${totalBufferCap}`} />
        {myOutposts.length > 0 && (
          <ActionButton
            label="Claim All Buffers"
            loading={pendingClaim}
            onClick={handleClaimAll}
            style={primaryButtonStyle}
          />
        )}
        {error && (
          <div style={inlineErrorStyle}>{error}</div>
        )}
      </Section>

      {/* Selected zone */}
      <Section title="Selected Zone">
        {!selection && (
          <div style={{ color: '#6a7c8c', fontSize: 11, fontStyle: 'italic' }}>
            Click anywhere on the globe to select a zone.
          </div>
        )}
        {selection && (
          <div>
            <Row label="Coords" value={`${selection.lat.toFixed(2)}, ${selection.lon.toFixed(2)}`} />

            {/* Existing structure detail */}
            {selected && (
              <SelectedStructure
                structure={selected}
                selfId={selfId}
                pendingDismantle={pendingDismantle}
                pendingToggle={pendingToggle}
                onDismantle={handleDismantle}
                onToggle={handleToggle}
                miningStats={miningStats[selected.id] ?? null}
                structures={structures}
              />
            )}

            {/* Build accordion for empty terrain */}
            {!selected && buildOptions.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ color: inOwnOutpost ? '#9fd8a3' : '#8a9caa', fontSize: 11, marginBottom: 6 }}>
                  {inOwnOutpost ? 'Within your Outpost radius' : 'Empty terrain'}
                </div>

                {/* Build toggle button */}
                <button
                  style={{
                    ...buildToggleStyle,
                    borderColor: buildOpen ? '#20a5a6' : '#2e4858',
                  }}
                  onClick={() => setBuildOpen(!buildOpen)}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      display: 'inline-block',
                      width: 0,
                      height: 0,
                      borderLeft: '5px solid transparent',
                      borderRight: '5px solid transparent',
                      borderTop: buildOpen ? '6px solid #20a5a6' : 'none',
                      borderBottom: buildOpen ? 'none' : '6px solid #20a5a6',
                      transition: 'all 0.15s',
                    }} />
                    Build Structure
                  </span>
                </button>

                {/* Accordion content */}
                {buildOpen && (
                  <div style={accordionStyle}>
                    {buildOptions.map((type) => {
                      const affordable = credits >= COSTS[type];
                      const isLoading = pendingBuild === type;
                      return (
                        <ActionButton
                          key={type}
                          label={
                            <span style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{
                                  display: 'inline-block',
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  background: STRUCTURE_COLORS[type],
                                  flexShrink: 0,
                                }} />
                                {STRUCTURE_LABELS[type]}
                              </span>
                              <span style={{ color: affordable ? '#9fd8a3' : '#dd9683' }}>
                                {COSTS[type].toLocaleString()} cr
                              </span>
                            </span>
                          }
                          loading={isLoading}
                          disabled={!affordable || (!!pendingBuild && !isLoading)}
                          onClick={() => handleBuild(type)}
                          style={{
                            ...buildItemStyle,
                            opacity: affordable && !pendingBuild ? 1 : isLoading ? 0.8 : 0.4,
                            cursor: affordable && !pendingBuild ? 'pointer' : 'not-allowed',
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Section>

      {/* Activity log */}
      <Section title="Recent Activity">
        <div style={{ maxHeight: 180, overflowY: 'auto' }}>
          <PlanetActivityLog />
        </div>
      </Section>

      {/* Leave */}
      <button style={leaveButtonStyle} onClick={onLeave}>
        Leave Planet [E]
      </button>
    </div>
  );
}

// ==================== SUBCOMPONENTS ====================

function Spinner() {
  return (
    <span style={spinnerStyle}>
      <style>{spinnerKeyframes}</style>
      <span style={spinnerDotStyle} />
    </span>
  );
}

function ActionButton({
  label,
  loading,
  disabled,
  onClick,
  style: btnStyle,
}: {
  label: React.ReactNode;
  loading: boolean;
  disabled?: boolean;
  onClick(): void;
  style: React.CSSProperties;
}) {
  return (
    <button
      style={btnStyle}
      disabled={disabled || loading}
      onClick={onClick}
    >
      {loading ? (
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <Spinner />
          Working...
        </span>
      ) : (
        label
      )}
    </button>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: '#20a5a6',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          marginBottom: 6,
        }}
      >
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | number }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '2px 0' }}>
      <span style={{ color: '#8a9caa' }}>{label}</span>
      <span style={{ color: '#f5e8d1' }}>{value}</span>
    </div>
  );
}

function SelectedStructure({
  structure,
  selfId,
  pendingDismantle,
  pendingToggle,
  onDismantle,
  onToggle,
  miningStats,
  structures,
}: {
  structure: StructureWire;
  selfId: string | null;
  pendingDismantle: string | null;
  pendingToggle: boolean;
  onDismantle(id: string): void;
  onToggle(id: string, active: boolean): void;
  miningStats: MiningStatsWire | null;
  structures: StructureWire[];
}) {
  const isOwn = structure.owner_player_id === selfId;
  const isMiner = structure.structure_type === 'mining_station';

  // Find parent outpost for mining stations
  const parentOutpost = isMiner && structure.parent_outpost_id
    ? structures.find((s) => s.id === structure.parent_outpost_id) ?? null
    : null;

  return (
    <div style={{ marginTop: 8, padding: 8, background: 'rgba(255,255,255,0.03)', borderRadius: 4 }}>
      <Row label="Type"   value={structure.structure_type.replace('_', ' ')} />
      <Row label="Owner"  value={isOwn ? 'You' : truncate(structure.owner_player_id, 8)} />
      <Row label="Status" value={structure.is_active ? 'Active' : 'Depleted'} />
      <Row label="Radius" value={`${structure.radius_km | 0} km`} />

      {/* Mining station: rich detail */}
      {isMiner && miningStats && (
        <div style={{ marginTop: 6 }}>
          {/* Organic channel */}
          <ResourceChannel
            label="Organic"
            color="#7cb342"
            resourceId={miningStats.organic.resourceId}
            tier={miningStats.organic.tier}
            richness={miningStats.organic.richness}
            rate={miningStats.organic.rate}
          />
          {/* Crystal channel */}
          <ResourceChannel
            label="Crystal"
            color="#29b6f6"
            resourceId={miningStats.crystal.resourceId}
            tier={miningStats.crystal.tier}
            richness={miningStats.crystal.richness}
            rate={miningStats.crystal.rate}
          />

          {/* Total mined */}
          {Object.keys(miningStats.totalMined).length > 0 && (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: 11, color: '#8a9caa', marginBottom: 2 }}>Total Mined</div>
              {Object.entries(miningStats.totalMined)
                .filter(([, qty]) => qty > 0)
                .map(([rid, qty]) => (
                  <Row key={rid} label={resourceName(rid)} value={`${Math.floor(qty as number).toLocaleString()}`} />
                ))}
            </div>
          )}

          {/* Parent outpost buffer */}
          {parentOutpost && (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: 11, color: '#8a9caa', marginBottom: 2 }}>
                Outpost Buffer ({sumBuffer(parentOutpost.buffer_contents)}/{parentOutpost.buffer_max})
              </div>
              {Object.entries(parentOutpost.buffer_contents ?? {})
                .filter(([, qty]) => (qty as number) > 0)
                .map(([rid, qty]) => (
                  <Row key={rid} label={resourceName(rid)} value={`${Math.floor(qty as number)}`} />
                ))}
              {Object.keys(parentOutpost.buffer_contents ?? {}).length === 0 && (
                <div style={{ fontSize: 11, color: '#6a7c8c', fontStyle: 'italic' }}>Empty</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Non-miner: simple resource display */}
      {!isMiner && structure.primary_resource_type && (
        <Row label="Resource" value={resourceName(structure.primary_resource_type)} />
      )}

      {structure.structure_type === 'outpost' && (
        <>
          <Row
            label="Buffer"
            value={`${sumBuffer(structure.buffer_contents)} / ${structure.buffer_max}`}
          />
          {isOwn && Object.entries(structure.buffer_contents ?? {}).filter(([, qty]) => (qty as number) > 0).length > 0 && (
            <ul style={{ margin: '4px 0', padding: '0 0 0 14px', fontSize: 11, color: '#a0c4d8' }}>
              {Object.entries(structure.buffer_contents)
                .filter(([, qty]) => (qty as number) > 0)
                .map(([rid, qty]) => (
                  <li key={rid}>
                    {resourceName(rid)}: {Math.floor(qty as number)}
                  </li>
                ))}
            </ul>
          )}
        </>
      )}
      {isOwn && isMiner && (
        <ActionButton
          label={structure.is_active ? 'Turn Off' : 'Turn On'}
          loading={pendingToggle}
          onClick={() => onToggle(structure.id, !structure.is_active)}
          style={structure.is_active ? toggleOffButtonStyle : toggleOnButtonStyle}
        />
      )}
      {isOwn && (
        <ActionButton
          label={`Dismantle (refund ${Math.floor(costFor(structure.structure_type) * PLANET_BALANCE.DISMANTLE_RECOVERY_PERCENT).toLocaleString()})`}
          loading={pendingDismantle === structure.id}
          onClick={() => onDismantle(structure.id)}
          style={dangerButtonStyle}
        />
      )}
    </div>
  );
}

/** Compact display for one resource channel (organic or crystal). */
function ResourceChannel({
  label,
  color,
  resourceId,
  tier,
  richness,
  rate,
}: {
  label: string;
  color: string;
  resourceId: string;
  tier: number;
  richness: number;
  rate: number;
}) {
  const pct = Math.min(100, richness * 100);
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
        <span style={{ color, fontWeight: 600 }}>
          {label} · T{tier}
        </span>
        <span style={{ color: '#f5e8d1' }}>
          {resourceName(resourceId)}
        </span>
      </div>
      {/* Richness bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
        <div style={{
          flex: 1, height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden',
        }}>
          <div style={{
            width: `${pct}%`, height: '100%', background: color, borderRadius: 2,
            opacity: pct > 0 ? 1 : 0.2,
          }} />
        </div>
        <span style={{ fontSize: 11, color: '#8a9caa', minWidth: 36, textAlign: 'right' }}>
          {pct.toFixed(0)}%
        </span>
      </div>
      {/* Rate */}
      <div style={{ fontSize: 11, color: '#6a7c8c', textAlign: 'right' }}>
        {rate > 0 ? `${rate.toFixed(2)} u/min` : 'depleted'}
      </div>
    </div>
  );
}

// ==================== HELPERS ====================

function structureAt(structures: StructureWire[], id: string | null): StructureWire | null {
  if (!id) return null;
  return structures.find((s) => s.id === id) ?? null;
}

function findOwnOutpostFor(
  structures: StructureWire[],
  selfId: string | null,
  lat: number,
  lon: number,
  _planetRadiusKm: number,
  freq: HexFrequency,
): StructureWire | null {
  if (!selfId) return null;
  // Phase 4+: hex-graph coverage. Direct outpost first, then relay chains.
  const targetHex = latLonToHexId(lat, lon, freq);
  const ownOutposts = structures.filter(
    (s) => s.structure_type === 'outpost' && s.owner_player_id === selfId,
  );
  for (const o of ownOutposts) {
    if (o.hex_id == null) continue;
    if (withinRings(o.hex_id, PLANET_BALANCE.OUTPOST_RINGS, freq).includes(targetHex)) {
      return o;
    }
  }
  for (const s of structures) {
    if (s.structure_type !== 'relay' || s.owner_player_id !== selfId) continue;
    if (s.hex_id == null || !s.parent_outpost_id) continue;
    if (withinRings(s.hex_id, PLANET_BALANCE.RELAY_RINGS, freq).includes(targetHex)) {
      const parent = ownOutposts.find((o) => o.id === s.parent_outpost_id);
      if (parent) return parent;
    }
  }
  return null;
}

function greatCircleKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
  radiusKm: number
): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δφ = toRad(lat2 - lat1);
  const Δλ = toRad(lon2 - lon1);
  const a =
    Math.sin(Δφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return radiusKm * c;
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + '…' : s;
}

function sumBuffer(buffer: Record<string, number>): number {
  return Math.floor(Object.values(buffer ?? {}).reduce((a, v) => a + (typeof v === 'number' ? v : 0), 0));
}

function costFor(type: 'outpost' | 'mining_station' | 'relay' | 'scanner'): number {
  return COSTS[type];
}

function resourceName(id: string): string {
  if (id in ORGANIC_RESOURCES) return (ORGANIC_RESOURCES as Record<string, { name: string }>)[id].name;
  if (id in CRYSTAL_RESOURCES) return (CRYSTAL_RESOURCES as Record<string, { name: string }>)[id].name;
  return id;
}

// ==================== STYLES ====================

const panelStyle: React.CSSProperties = {
  width: '35vw',
  minWidth: 360,
  maxWidth: 540,
  flexShrink: 0,
  boxSizing: 'border-box',
  height: '100%',
  padding: '16px 20px 16px 16px',
  background: 'rgba(15, 27, 38, 0.95)',
  color: '#f5e8d1',
  fontFamily: "'JetBrains Mono', monospace",
  borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
};

const headerStyle: React.CSSProperties = {
  marginBottom: 16,
  paddingBottom: 12,
  borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
};

const inlineErrorStyle: React.CSSProperties = {
  marginTop: 6,
  padding: '4px 8px',
  color: '#ff8866',
  fontSize: 11,
  fontFamily: "'JetBrains Mono', monospace",
};

const primaryButtonStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '8px 12px',
  marginTop: 6,
  background: '#1a2a38',
  color: '#9fd8a3',
  border: '1px solid #2e4858',
  borderRadius: 4,
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 11,
  cursor: 'pointer',
};

const dangerButtonStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '6px 10px',
  marginTop: 8,
  background: '#2a1a1a',
  color: '#dd9683',
  border: '1px solid #5b2a2a',
  borderRadius: 4,
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 11,
  cursor: 'pointer',
};

const toggleOffButtonStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '6px 10px',
  marginTop: 8,
  background: '#1a2a2a',
  color: '#7a7a7a',
  border: '1px solid #3a4a4a',
  borderRadius: 4,
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 11,
  cursor: 'pointer',
};

const toggleOnButtonStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '6px 10px',
  marginTop: 8,
  background: '#1a2a1a',
  color: '#3ddc84',
  border: '1px solid #2a5b2a',
  borderRadius: 4,
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 11,
  cursor: 'pointer',
};

const buildToggleStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: '8px 12px',
  background: '#152232',
  color: '#20a5a6',
  border: '1px solid #2e4858',
  borderRadius: 4,
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'border-color 0.15s',
};

const accordionStyle: React.CSSProperties = {
  marginTop: 2,
  padding: '4px 0',
  borderLeft: '2px solid #2e4858',
  marginLeft: 6,
  paddingLeft: 8,
};

const buildItemStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '7px 10px',
  marginTop: 4,
  background: '#1a2a38',
  color: '#f5e8d1',
  border: '1px solid #2e4858',
  borderRadius: 4,
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 11,
  cursor: 'pointer',
  textAlign: 'left',
};

const spinnerKeyframes = `
@keyframes planet-spinner {
  0% { opacity: 0.3; }
  50% { opacity: 1; }
  100% { opacity: 0.3; }
}`;

const spinnerStyle: React.CSSProperties = {
  display: 'inline-flex',
  gap: 3,
};

const spinnerDotStyle: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: 'currentColor',
  animation: 'planet-spinner 0.8s ease-in-out infinite',
};

const leaveButtonStyle: React.CSSProperties = {
  marginTop: 'auto',
  padding: '10px 12px',
  background: '#2a3a48',
  color: '#f5e8d1',
  border: '1px solid #4a5a6a',
  borderRadius: 4,
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 11,
  cursor: 'pointer',
};
